import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactFlow, MiniMap, Controls, Background, useNodesState, useEdgesState, Panel, NodeMouseHandler, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { LdgGraphNode } from './LdgGraphNode';
import { NodeDetailPanel } from './NodeDetailPanel';
import { useLdgGraphData, LdgNodeData } from './useLdgGraphData';
import { MODULES } from '../../../../content';
import { Filter, GitBranch, ChevronDown, ChevronUp, X } from 'lucide-react';
const nodeTypes = {
  ldgNode: LdgGraphNode
};
function LdgGraphExplorerInner() {
  const [activeModules, setActiveModules] = useState<Set<string>>(new Set());
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const {
    nodes: initialNodes,
    edges: initialEdges,
    stats
  } = useLdgGraphData(activeModules);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);
  const toggleModule = (moduleId: string) => {
    setActiveModules(prev => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
    setSelectedNodeId(null);
  };
  const onNodeClick: NodeMouseHandler = useCallback((_, node) => {
    setSelectedNodeId(node.id);
  }, []);
  const handleSelectNode = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId);
  }, []);
  const selectedNodeData = useMemo(() => {
    if (!selectedNodeId) return null;
    const node = nodes.find(n => n.id === selectedNodeId);
    return node ? node.data as LdgNodeData : null;
  }, [selectedNodeId, nodes]);
  const activeFilterCount = activeModules.size;
  return <div className="w-full h-full relative bg-slate-50 flex flex-col" data-id="element-5815" data-mp="184rkzh450ss">
      {/* ── Mobile: Compact top bar ── */}
      <div className="md:hidden flex-shrink-0 bg-white/95 backdrop-blur-md border-b border-slate-200 z-20" data-id="element-5816" data-mp="3sepnawi44ww5">
        {/* Stats row */}
        <div className="flex items-center justify-between px-3 py-2" data-id="element-5817" data-mp="a7jiu153tfec">
          <div className="flex items-center gap-3" data-id="element-5818" data-mp="5989rsmmv8x6">
            <div className="flex items-center gap-1.5" data-id="element-5819" data-mp="57nchv8xpbvm">
              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" data-id="element-5820" data-mp="3dorto11ezezk" />
              <span className="text-xs font-bold text-slate-700" data-id="element-5821" data-mp="39o8017g1uti6">
                {stats.nodeCount}
              </span>
              <span className="text-[10px] text-slate-400" data-id="element-5822" data-mp="3nfmjffdvcwpr">nodes</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-5823" data-mp="3io4j8tjo705u">
              <div className="w-1.5 h-1.5 rounded-full bg-slate-400" data-id="element-5824" data-mp="3imjnj0lmdb6e" />
              <span className="text-xs font-bold text-slate-700" data-id="element-5825" data-mp="2uxqzdqb8o7mc">
                {stats.edgeCount}
              </span>
              <span className="text-[10px] text-slate-400" data-id="element-5826" data-mp="2pzp6yerxavy7">edges</span>
            </div>
            <div className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${stats.isValidDAG ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-5827" data-mp="15gdccgli2he9">
              {stats.isValidDAG ? 'DAG ✓' : 'DAG ✗'}
            </div>
          </div>
          <button onClick={() => setPanelOpen(!panelOpen)} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${panelOpen ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`} data-id="element-5828" data-mp="11fjkjc4xsig3">
            <Filter className="w-3.5 h-3.5" data-id="element-5829" data-mp="1f7zu6vscfepz" />
            Filter
            {activeFilterCount > 0 && <span className="bg-indigo-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-id="element-5830" data-mp="1k6b4t8qgpt99">
                {activeFilterCount}
              </span>}
            {panelOpen ? <ChevronUp className="w-3 h-3" data-id="element-5831" data-mp="8sgpwv3dsrv4" /> : <ChevronDown className="w-3 h-3" data-id="element-5832" data-mp="o1u8ntpjwvsb" />}
          </button>
        </div>

        {/* Collapsible filter chips */}
        {panelOpen && <div className="px-3 pb-3 border-t border-slate-100 pt-2" data-id="element-5833" data-mp="j8hk1jkvgozk">
            <div className="flex flex-wrap gap-1.5" data-id="element-5834" data-mp="f9sak22yfrl7">
              {MODULES.map(mod => {
            const isActive = activeModules.size === 0 || activeModules.has(mod.id);
            return <button key={mod.id} onClick={() => toggleModule(mod.id)} className={`px-2 py-1 rounded-full text-[11px] font-medium transition-colors border ${isActive ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200'}`} data-id="element-5835" data-mp="16y2hf7hrvosd">
                    {mod.id}
                  </button>;
          })}
            </div>
            {activeModules.size > 0 && <button onClick={() => setActiveModules(new Set())} className="mt-2 text-xs font-medium text-indigo-600 w-full text-center py-1 bg-indigo-50 rounded-lg" data-id="element-5836" data-mp="3dpbbwqroxlkg">
                Toon alles
              </button>}
          </div>}
      </div>

      {/* ── Desktop: Floating panels (hidden on mobile) ── */}
      <div className="hidden md:flex absolute top-4 left-4 z-10 flex-col gap-3 pointer-events-none" data-id="element-5837" data-mp="3icdnqsgfnnfu">
        {/* Stats Panel */}
        <div className="bg-white/90 backdrop-blur-md p-3 rounded-xl shadow-sm border border-slate-200 pointer-events-auto w-72" data-id="element-5838" data-mp="3mf9zq0bgwlat">
          <div className="flex items-center gap-2 mb-2.5" data-id="element-5839" data-mp="68r7sp58cbj">
            <div className="p-1 bg-indigo-100 text-indigo-600 rounded-lg" data-id="element-5840" data-mp="6qsx4ukriqu">
              <GitBranch className="w-4 h-4" data-id="element-5841" data-mp="53r23omq11m6" />
            </div>
            <div data-id="element-5842" data-mp="21xlte9zq863w">
              <h2 className="font-bold text-sm text-slate-900 leading-tight" data-id="element-5843" data-mp="21y4060k0m2q2">
                Learning Dependency Graph
              </h2>
              <p className="text-[10px] text-slate-500" data-id="element-5844" data-mp="26woz7wnj6gcx">
                Canonical Taxonomy Structure
              </p>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-1.5" data-id="element-5845" data-mp="1txfthnsj6ep7">
            <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100 text-center" data-id="element-5846" data-mp="1tyqyts1mjuif">
              <div className="text-[9px] font-bold text-slate-400 uppercase" data-id="element-5847" data-mp="28ru34xq7gbr7">
                Nodes
              </div>
              <div className="font-bold text-sm text-slate-700" data-id="element-5848" data-mp="2hulj9au0zvp6">
                {stats.nodeCount}
              </div>
            </div>
            <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100 text-center" data-id="element-5849" data-mp="2cuh0fe5naovs">
              <div className="text-[9px] font-bold text-slate-400 uppercase" data-id="element-5850" data-mp="2rkoab5hl6zkv">
                Edges
              </div>
              <div className="font-bold text-sm text-slate-700" data-id="element-5851" data-mp="2mmmtysbjped3">
                {stats.edgeCount}
              </div>
            </div>
            <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100 text-center" data-id="element-5852" data-mp="30iqfzyc9mgeh">
              <div className="text-[9px] font-bold text-slate-400 uppercase" data-id="element-5853" data-mp="2whoetbysfu9v">
                Depth
              </div>
              <div className="font-bold text-sm text-slate-700" data-id="element-5854" data-mp="3k6mo21ghhwsl">
                {stats.maxDepth}
              </div>
            </div>
            <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100 text-center" data-id="element-5855" data-mp="36626t9q7w917">
              <div className="text-[9px] font-bold text-slate-400 uppercase" data-id="element-5856" data-mp="3b55c8va40h9p">
                DAG
              </div>
              <div className={`font-bold text-sm ${stats.isValidDAG ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-5857" data-mp="2wfr1biqvxrs7">
                {stats.isValidDAG ? '✓' : '✗'}
              </div>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        <div className="bg-white/90 backdrop-blur-md p-3 rounded-xl shadow-sm border border-slate-200 pointer-events-auto w-72" data-id="element-5858" data-mp="2rj0kgt79as5q">
          <div className="flex items-center gap-2 mb-2" data-id="element-5859" data-mp="2dkj1f8saq3u5">
            <Filter className="w-3.5 h-3.5 text-slate-400" data-id="element-5860" data-mp="2ighj6dj4cnll" />
            <h3 className="font-bold text-xs text-slate-700" data-id="element-5861" data-mp="2xzk6eghxre1b">
              Filter op Module
            </h3>
          </div>
          <div className="flex flex-wrap gap-1.5" data-id="element-5862" data-mp="2szxqu0emxlpc">
            {MODULES.map(mod => {
            const isActive = activeModules.size === 0 || activeModules.has(mod.id);
            return <button key={mod.id} onClick={() => toggleModule(mod.id)} className={`px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors border ${isActive ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-5863" data-mp="2jypxzxuw1d2n">
                  {mod.id}
                </button>;
          })}
          </div>
          {activeModules.size > 0 && <button onClick={() => setActiveModules(new Set())} className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors w-full text-center py-1 bg-indigo-50 rounded-lg" data-id="element-5864" data-mp="1w8vnvo1ye2m3">
              Toon alles
            </button>}
        </div>
      </div>

      {/* ReactFlow Canvas */}
      <div className="flex-1 w-full h-full min-h-0" data-id="element-5865" data-mp="gqpkfj9jl39k">
        <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onNodeClick={onNodeClick} nodeTypes={nodeTypes} fitView fitViewOptions={{
        padding: 0.2
      }} minZoom={0.05} maxZoom={1.5} defaultEdgeOptions={{
        zIndex: 0
      }} data-id="element-5866" data-mp="gs0lvcfilyxi">
          <Background color="#cbd5e1" gap={20} size={1} data-id="element-5867" data-mp="1tyjv6o4tlid" />
          <Controls className="bg-white border-slate-200 shadow-sm rounded-lg overflow-hidden" showInteractive={false} data-id="element-5868" data-mp="1vjdmu2mh0f6" />
          <MiniMap className="hidden md:block bg-white border-slate-200 shadow-sm rounded-xl overflow-hidden" nodeColor={node => {
          const data = node.data as LdgNodeData;
          return data.colorSet?.ring || '#cbd5e1';
        }} maskColor="rgba(248, 250, 252, 0.7)" data-id="element-5869" data-mp="35ite85asn4o9" />
        </ReactFlow>
      </div>

      {/* Detail Panel */}
      <NodeDetailPanel nodeData={selectedNodeData} onClose={() => setSelectedNodeId(null)} onSelectNode={handleSelectNode} data-id="element-5870" data-mp="3m24xnsuhbnjt" />
    </div>;
}
export function LdgGraphExplorer() {
  return <ReactFlowProvider data-id="element-5871" data-mp="2tcy314y5bi3u">
      <LdgGraphExplorerInner data-id="element-5872" data-mp="2te93ausdu7q7" />
    </ReactFlowProvider>;
}